import { BusinessPreview } from '@/components/organisms/BusinessPreview';
import { cn } from '@/lib/classNames';
import { useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import { useAddContactDataMutation } from './hooks/useAddContactDataMutation';
import { useGetEmailQuery } from './hooks/useGetEmailQuery';
import { useReminderMutation } from './hooks/useReminderMutation';
import { useRemoveContactDataMutation } from './hooks/useRemoveContactDataMutation';
import { useRemoveNoteMutation } from './hooks/useRemoveNoteMutation';
import { useReportDataMutation } from './hooks/useReportDataMutation';
import { useTaskData } from './hooks/useTaskData';
import { useUserData } from './hooks/useUserData';
import { useSaveNoteMutation } from './hooks/useSaveNoteMutation';
import { useSelectBusinessMutation } from './hooks/useSelectBusinessMutation';
import { useSelectedBusinessData } from './hooks/useSelectedBusinessData';

type SearchBusinessesBusinessPreviewProps = {
	onGoToMap?: (businessId: string) => void;
};

export const SearchBusinessesBusinessPreview = ({
	onGoToMap
}: SearchBusinessesBusinessPreviewProps) => {
	const query = useSelector(
		(state: RootState) => state.dashboard.get('query').toJS(),
		shallowEqual
	);
	const { selectedBusiness } = useSelectedBusinessData();
	const { user } = useUserData();

	const {
		isError: isTaskError,
		isLoading: isTaskLoading,
		isSuccess: isTaskSuccess
	} = useTaskData();
	const { onResetReminder, onDeleteReminder, onSetReminder } =
		useReminderMutation();

	const { mutate: mutateSaveNote } = useSaveNoteMutation();
	const { mutate: mutateGetEmailQuery } = useGetEmailQuery();
	const { mutate: mutateSelectBusiness } = useSelectBusinessMutation();
	const {
		mutate: mutateReportData,
		isError: isReportIncorrectDataError,
		isLoading: isReportIncorrectDataLoading,
		isSuccess: isReportIncorrectDataSuccess
	} = useReportDataMutation();
	const { mutate: mutateRemoveNote } = useRemoveNoteMutation();
	const {
		mutate: mutateAddContactData,
		isError: isContactDataError,
		isLoading: isContactDataLoading,
		isSuccess: isContactDataSuccess
	} = useAddContactDataMutation();
	const { mutate: mutateRemoveContactData } = useRemoveContactDataMutation();

	const lastBusinessIdRef = useRef<string | null>(null);

	return (
		<div
			data-is-visible={Boolean(
				selectedBusiness?._id ||
					selectedBusiness?.businessId ||
					selectedBusiness?.id
			)}
			className={cn(
				'absolute right-0 top-0 z-20 flex h-full max-w-[31rem] flex-col overflow-hidden rounded-tl-lg border-l border-t border-dm-grey bg-dm-black3 shadow-md 3xl:relative',
				'w-0 translate-x-full transition-[transform,width,margin-left] duration-300 ease-in-out',
				'data-[is-visible=true]:ml-4 data-[is-visible=true]:w-full data-[is-visible=true]:translate-x-0'
			)}
		>
			<BusinessPreview
				lastBusinessIdRef={lastBusinessIdRef}
				listId={selectedBusiness?.listId}
				user={user}
				selectedBusiness={selectedBusiness}
				isReportIncorrectDataError={isReportIncorrectDataError}
				isReportIncorrectDataLoading={isReportIncorrectDataLoading}
				isReportIncorrectDataSuccess={isReportIncorrectDataSuccess}
				onSaveNote={mutateSaveNote}
				onSelectBusiness={mutateSelectBusiness}
				onGetEmailThread={mutateGetEmailQuery}
				onReportIncorrectData={mutateReportData}
				onRemoveNote={mutateRemoveNote}
				onRemoveContactData={mutateRemoveContactData}
				onAddContactData={mutateAddContactData}
				isContactDataError={isContactDataError}
				isContactDataLoading={isContactDataLoading}
				isContactDataSuccess={isContactDataSuccess}
				isTaskLoading={isTaskLoading}
				isTaskError={isTaskError}
				isTaskSuccess={isTaskSuccess}
				onDeleteReminder={onDeleteReminder}
				onResetReminder={onResetReminder}
				onSetReminder={onSetReminder}
				appliedFilters={query}
				goToMap={onGoToMap}
			/>
		</div>
	);
};
